// The ten recovery codes, on the glass — spec 15 §4 S2's tail ("recovery codes
// shown once with forced 'I saved them' checkbox") and the same presentation
// reused by the on-demand reissue on `/console` (§5
// `POST auth/recovery-codes/regenerate`).
//
// THIS IS THE SCREEN THAT MOST NEEDS TO BE RIGHT. The ten codes are the only
// way back into an account whose phone is lost, the server keeps only their
// hashes, and no endpoint will ever return THESE again — a reissue mints a NEW
// batch and voids them, and it needs a working authenticator, which is exactly
// what the person who needs a recovery code does not have. So:
//
//   * the consequence is stated before the codes, in the warning register;
//   * copy and download are one click each, so "I'll do it later" has no excuse;
//   * the codes are NUMBERED — someone is transcribing ten opaque strings by
//     hand onto paper, and "7 of 10" is the difference between confident and
//     anxious;
//   * Continue is disabled behind a deliberate acknowledgement, and the
//     acknowledgement sentence names what is being acknowledged rather than
//     saying "OK";
//   * and leaving by ANY other door — back, reload, closing the tab, typing a
//     URL — asks first, because the checkbox gates only the button and every
//     one of those exits was silent.
//
// WHAT THE ASK CANNOT DO is make this a one-way door, and it must not pretend
// to: `beforeunload` cannot be cancelled by script and a router back is not an
// unload at all. That is why `/console` carries the reissue control — the
// belt-and-braces answer to "I clicked past it" is a way to mint a new batch,
// not a cage. Both halves ship together; neither is sufficient alone.
//
// NOTHING HERE PERSISTS THE CODES. They live in the caller's React state and in
// nothing else: not localStorage, not sessionStorage, not the URL, not a log.
// The download builds the file in memory from an object URL revoked in the same
// tick, and the clipboard write is the user's own explicit action.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";

export interface RecoveryCodesProps {
  codes: readonly string[];
  /** What the primary does once the codes are acknowledged. */
  onDone: () => void;
  /** The primary's label — "Continue to the console" / "Done". */
  doneLabel: string;
}

export function RecoveryCodes({ codes, onDone, doneLabel }: RecoveryCodesProps) {
  const { t } = useTranslation();
  const [acked, setAcked] = useState(false);
  const [nagging, setNagging] = useState(false);
  const [copied, setCopied] = useState(false);

  const asText = codes.join("\n");

  // The browser's own "leave site?" prompt, armed only while the codes are
  // UNACKNOWLEDGED. Chrome ignores the custom string and shows its own wording,
  // which is fine — the value of this is the pause, not the sentence. It
  // disarms the moment the box is ticked so a user who has saved them is never
  // nagged, and it is removed on unmount so it cannot outlive the screen.
  useEffect(() => {
    if (acked) return undefined;
    const warn = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // DEPRECATED AND KEPT ON PURPOSE. `preventDefault()` alone is the current
      // standard and is what Chromium and Firefox honour; Safari still gates
      // the prompt on a truthy `returnValue`, and this screen is the one place
      // in the product where losing the prompt loses data that cannot be
      // recovered. The lint rule is right in general and wrong here.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
    };
  }, [acked]);

  const copy = () => {
    // `navigator.clipboard` is typed as always present but is genuinely absent
    // in an insecure context, so the widened local is the honest type — and it
    // is what lets the guard below be a real check rather than dead code.
    const { clipboard } = navigator as unknown as { clipboard?: Clipboard };
    if (clipboard === undefined) {
      setCopied(false);
      return;
    }
    void clipboard.writeText(asText).then(
      () => {
        setCopied(true);
      },
      () => {
        // A refused clipboard (denied permission) is not an error worth a
        // banner: the codes are selectable text on the glass and the download
        // is right beside this button.
        setCopied(false);
      },
    );
  };

  const download = () => {
    const blob = new Blob([`${t("admin.recovery.fileHeader")}\n\n${asText}\n`], {
      type: "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = t("admin.recovery.fileName");
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="admin-once">
        <p className="admin-once-t">{t("admin.recovery.warningTitle")}</p>
        <p className="admin-once-b">{t("admin.recovery.warningBody")}</p>
      </div>

      <ol
        className="admin-codes"
        aria-label={t("admin.recovery.listLabel")}
        data-testid="admin-recovery-codes"
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>

      <div className="admin-actions">
        <Button onClick={copy} data-testid="admin-recovery-copy">
          {copied ? t("admin.recovery.copied") : t("admin.recovery.copy")}
        </Button>
        <Button onClick={download} data-testid="admin-recovery-download">
          {t("admin.recovery.download")}
        </Button>
      </div>

      <label className="admin-ack" data-testid="admin-recovery-ack-label">
        <input
          type="checkbox"
          checked={acked}
          data-testid="admin-recovery-ack"
          onChange={(e) => {
            setAcked(e.target.checked);
            if (e.target.checked) setNagging(false);
          }}
        />
        <span>{t("admin.recovery.ack")}</span>
      </label>

      {nagging ? (
        <p className="admin-err" role="alert" data-testid="admin-recovery-ack-required">
          {t("admin.recovery.ackRequired")}
        </p>
      ) : null}

      <div className="admin-actions">
        <Button
          variant="primary"
          disabled={!acked}
          onClick={() => {
            if (!acked) {
              setNagging(true);
              return;
            }
            onDone();
          }}
          data-testid="admin-recovery-continue"
        >
          {doneLabel}
        </Button>
      </div>
    </>
  );
}
